import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getScrapeConfigs,
  getScrapeConfig,
  createScrapeConfig,
  updateScrapeConfig,
  deleteScrapeConfig,
  triggerScrapeRun,
  getScrapeRuns,
  getAtsOptions,
} from "../Services/API/Scrape";
import { CreateScrapeConfig, UpdateScrapeConfig, ScrapeConfigFilters } from "../Types/Scrapes";

export const useScrapeConfigsQuery = (filters?: ScrapeConfigFilters) => {
  return useQuery(
    ["scrapeConfigs", filters],
    () => getScrapeConfigs(filters),
    { staleTime: 1000 * 60 }
  );
};

export const useScrapeConfigQuery = (id: string) => {
  return useQuery(
    ["scrapeConfig", id],
    () => getScrapeConfig(id),
    { enabled: !!id, staleTime: 1000 * 60 }
  );
};

export const useAtsOptionsQuery = () => {
  return useQuery(["atsOptions"], () => getAtsOptions(), {
    staleTime: 1000 * 60 * 30,
  });
};

export const useScrapeRunsQuery = (configId: string, enabled: boolean = false) => {
  return useQuery(
    ["scrapeRuns", configId],
    () => getScrapeRuns(configId),
    { enabled, staleTime: 1000 * 30 }
  );
};

export const useCreateConfigMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateScrapeConfig) => createScrapeConfig(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scrapeConfigs"]);
      },
    }
  );
};

export const useUpdateConfigMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: UpdateScrapeConfig }) => updateScrapeConfig(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scrapeConfigs"]);
      },
    }
  );
};

export const useDeleteConfigMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => deleteScrapeConfig(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scrapeConfigs"]);
      },
    }
  );
};

export const useTriggerScrapeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => triggerScrapeRun(id),
    {
      onSuccess: (_data, id) => {
        queryClient.invalidateQueries(["scrapeRuns", id]);
      },
    }
  );
};
